export const generateMultipleChoicePrompt = (eventDetails, stats, posts = []) => {
  console.log('⚙️ generateMultipleChoicePrompt called with:', {
    hasEventDetails: !!eventDetails,
    eventDetailsKeys: eventDetails ? Object.keys(eventDetails) : [],
    hasStats: !!stats,
    statsKeys: stats ? Object.keys(stats) : [],
    hasPosts: !!posts,
    postsCount: Array.isArray(posts) ? posts.length : 0,
    timestamp: new Date().toISOString()
  });

  // Validate inputs more carefully
  if (!eventDetails) {
    console.error('❌ Missing eventDetails in generateMultipleChoicePrompt');
    return null;
  }
  
  // Return generic fallback if stats is missing
  if (!stats) {
    console.warn('⚠️ Missing stats in generateMultipleChoicePrompt, returning fallback prompt');
    return `You are an expert data analyst analyzing a Multiple Choice survey question. Please provide general analysis principles for multiple choice questions and best practices for interpreting such data.`;
  }
  
  console.log('📊 Stats object structure:', {
    hasStats: !!stats,
    statsKeys: Object.keys(stats),
    timestamp: new Date().toISOString()
  });

  try {
    // Format event details and stats for the prompt with better defaults
    const eventName = eventDetails?.name || eventDetails?.title || 'Untitled Event';
    const questionType = eventDetails?.question_type || 'multiple_choice';
    const description = eventDetails?.description || eventDetails?.details || 'No description available';
    
    // Access metrics data from the correct path
    const metricsData = stats?.metrics || {};
    const summaryData = stats?.summary || {};
    
    console.log('📊 MultipleChoice prompt - accessing metrics:', { 
      hasMetrics: !!metricsData, 
      metricsDataKeys: Object.keys(metricsData),
      hasSummary: !!summaryData,
      summaryDataKeys: Object.keys(summaryData),
      timestamp: new Date().toISOString() 
    });
    
    // Extract total responses with improved fallback paths
    let totalResponses = 0;
    
    // Try all possible paths for total responses
    if (metricsData && typeof metricsData === 'object') {
      totalResponses = metricsData.contributed || metricsData.choices || metricsData.responses || 
                      metricsData.ideas || metricsData.totalResponses || metricsData.total || 0;
    }
    
    // If summary has total_choices, use that as a fallback
    if (totalResponses === 0 && summaryData && typeof summaryData === 'object') {
      totalResponses = summaryData.total_choices || summaryData.total_responses || 
                      summaryData.choices || summaryData.responses || summaryData.total || 0;
    }
    
    // Check the top level stats object for response counts
    if (totalResponses === 0) {
      totalResponses = stats.total_choices || stats.total_responses || stats.choices || 
                      stats.responses || stats.respondents || stats.contributed || 0;
    }
    
    console.log('🔎 Multiple choice - searching for totalResponses:', {
      responses: totalResponses,
      statsKeys: Object.keys(stats),
      metricsKeys: metricsData ? Object.keys(metricsData) : [],
      summaryKeys: summaryData ? Object.keys(summaryData) : [],
      timestamp: new Date().toISOString()
    });
    
    // Get total users with enhanced fallbacks
    let totalUsers = metricsData.total_user || metricsData.viewed_user || metricsData.joined || 
                    summaryData.total_user || summaryData.users || summaryData.participants || 
                    stats.total_user || stats.users || stats.participants || 0;
    
    // If we have response data but no user count, estimate conservatively
    if (totalUsers === 0 && totalResponses > 0) {
      totalUsers = Math.max(Math.ceil(totalResponses * 1.2), totalResponses + 3);
      console.log('⚠️ Multiple choice - estimated totalUsers:', totalUsers);
    }
    
    let participationRate = totalUsers > 0 ? Math.round((totalResponses / totalUsers) * 100) : 0;
    
    // For scores, maintain null fallbacks for values that might not apply
    const averageScore = stats?.summary?.simple_score ?? null;
    const minScore = stats?.summary?.min_score ?? null;
    // Not needed for multiple choice
    
    console.log('📊 Multiple choice metrics extraction:', {
      totalResponses,
      totalUsers,
      participationRate,
      timestamp: new Date().toISOString()
    });
    
    // Extract option distribution with enhanced fallback mechanisms
    let options = [];
    
    console.log('📊 Multiple choice - examining all possible data paths for options');
    
    // Try summary.ideas first (most common location for multiple choice data)
    if (summaryData?.ideas && Array.isArray(summaryData.ideas)) {
      console.log('📊 Extracting options from summary.ideas:', summaryData.ideas);
      
      options = summaryData.ideas.map(idea => ({
        title: idea.title || idea.name || 'Unnamed Option',
        count: idea.choice_count || idea.count || 0,
        percentage: idea.choice_pct ? parseInt(String(idea.choice_pct).replace('%', '')) :
                   (totalResponses > 0 ? Math.round(((idea.choice_count || idea.count || 0) / totalResponses) * 100) : 0),
        engagement: idea.engagement || 0,
        comments: idea.comments || 0
      }));
    }
    // Try direct choices property next
    else if (stats?.choices && typeof stats.choices === 'object') {
      console.log('📊 Extracting options from stats.choices:', stats.choices);
      
      options = Object.entries(stats.choices).map(([key, value]) => ({
        title: key,
        count: value || 0,
        percentage: totalResponses > 0 ? Math.round((value || 0) / totalResponses * 100) : 0,
        engagement: 0,
        comments: 0
      }));
    }
    // Try results/metrics choice_distribution
    else if (stats?.results?.choice_distribution && typeof stats.results.choice_distribution === 'object') {
      console.log('📊 Extracting options from results.choice_distribution:', stats.results.choice_distribution);
      
      options = Object.entries(stats.results.choice_distribution).map(([key, value]) => ({
        title: key,
        count: value || 0,
        percentage: totalResponses > 0 ? Math.round((value || 0) / totalResponses * 100) : 0,
        engagement: 0,
        comments: 0
      }));
    }
    // Try items array
    else if (stats?.items && Array.isArray(stats.items)) {
      console.log('📊 Extracting options from stats.items:', stats.items);
      options = stats.items.map(item => ({
        title: item.title || item.name || 'Unnamed Option',
        count: item.count || 0,
        percentage: item.percentage || (totalResponses > 0 ? Math.round((item.count || 0) / totalResponses * 100) : 0),
        engagement: item.engagement || 0,
        comments: item.comments || 0
      }));
    }
    // Try options array
    else if (stats?.options && Array.isArray(stats.options)) {
      console.log('📊 Extracting options from stats.options:', stats.options);
      options = stats.options.map(option => ({
        title: option.title || option.name || 'Unnamed Option',
        count: option.count || 0,
        percentage: option.percentage || (totalResponses > 0 ? Math.round((option.count || 0) / totalResponses * 100) : 0),
        engagement: option.engagement || 0,
        comments: option.comments || 0
      }));
    }
    // Try responses if available 
    else if (stats?.responses && Array.isArray(stats.responses)) {
      console.log('📊 Extracting options from aggregated responses');
      
      // Group responses by value and count occurrences
      const responseCounts = {};
      stats.responses.forEach(response => {
        const value = response.value || response.choice || response.answer || 'Unknown';
        responseCounts[value] = (responseCounts[value] || 0) + 1;
      });
      
      options = Object.entries(responseCounts).map(([key, count]) => ({
        title: key,
        count: count,
        percentage: totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0,
        engagement: 0,
        comments: 0
      }));
    }
    
    // Create fallback data if no options found, or validate existing options
    if (!options || options.length === 0) {
      console.log('⚠️ Multiple choice - no options found, creating fallback dummy data');
      options = [
        {
          title: 'Option A',
          count: 1,
          percentage: 50,
          engagement: 0,
          comments: 0
        },
        {
          title: 'Option B',
          count: 1,
          percentage: 50,
          engagement: 0,
          comments: 0
        }
      ];
    } else {
      // Ensure all options have non-zero counts if we have responses
      if (totalResponses > 0) {
        const allZeros = options.every(option => option.count === 0);
        if (allZeros) {
          console.log('⚠️ Multiple choice - all options have zero counts, distributing totalResponses');
          
          // Distribute total responses among options proportionally
          const perOption = Math.floor(totalResponses / options.length);
          const remainder = totalResponses % options.length;
          
          options = options.map((option, index) => ({
            ...option,
            count: perOption + (index === 0 ? remainder : 0),
            percentage: Math.round((perOption + (index === 0 ? remainder : 0)) / totalResponses * 100)
          }));
        }
      }
      
      // Validate percentages sum to ~100%
      const totalPercentage = options.reduce((sum, option) => sum + option.percentage, 0);
      if (totalPercentage > 0 && Math.abs(totalPercentage - 100) > 5) {
        console.log(`⚠️ Multiple choice - percentages sum to ${totalPercentage}, normalizing to 100%`);
        
        // Normalize percentages to sum to 100%
        options = options.map(option => ({
          ...option,
          percentage: Math.round((option.percentage / totalPercentage) * 100)
        }));
      }
    }
    
    // Update totalResponses if we have valid options but zero totalResponses
    if (totalResponses === 0 && options.length > 0 && options.some(option => option.count > 0)) {
      totalResponses = options.reduce((sum, option) => sum + option.count, 0);
      console.log('⚠️ Multiple choice - recalculated totalResponses from options:', totalResponses);
      
      // Recalculate participation rate
      participationRate = totalUsers > 0 ? Math.round((totalResponses / totalUsers) * 100) : 0;
    }
    
    // If we found options but percentages are missing, calculate them
    if (options.length > 0) {
      const totalOptionCount = options.reduce((sum, option) => sum + option.count, 0);
      if (totalOptionCount > 0) {
        options = options.map(option => ({
          ...option,
          percentage: option.percentage || Math.round((option.count / totalOptionCount) * 100)
        }));
        
        // Cross-validate totalResponses with option counts
        // If totalResponses is 0 but we have option data, use that to calculate a more accurate total
        if (totalResponses === 0 && totalOptionCount > 0) {
          totalResponses = totalOptionCount;
          console.log('📊 Recalculated totalResponses from option counts:', {
            totalOptionCount,
            newTotalResponses: totalResponses,
            timestamp: new Date().toISOString()
          });
          
          // Update participation rate with new total responses
          if (totalUsers > 0) {
            participationRate = Math.round((totalResponses / totalUsers) * 100);
          }
        }
      }
    }
    
    console.log('📊 Final options extraction:', {
      optionsCount: options.length,
      optionsSample: options.slice(0, 3), // Log first few options as sample
      timestamp: new Date().toISOString()
    });
    
    // Sort options by popularity (highest first)
    options.sort((a, b) => b.count - a.count);
    
    // Extract user segment data directly
    const userSegments = {
      basicUsers: stats?.basic_user || 0,
      heroUsers: stats?.hero_user || 0,
      earlyAccessUsers: stats?.early_access_user || 0,
      observerUsers: stats?.observer_user || 0
    };
    
    // Extract platform distribution directly

    // Format options breakdown
    let formattedOptions = '';
    if (options && options.length > 0) {
      formattedOptions = options.map((option, idx) =>
        `${idx + 1}. ${option.title}: ${option.count} responses (${option.percentage}%) | Engagement: ${option.engagement} | Comments: ${option.comments}`
      ).join('\n');
    } else {
      formattedOptions = 'No option data available.';
    }

    // Format user comments if available
    let formattedComments = '';
    if (Array.isArray(posts) && posts.length > 0) {
      // Show up to 3 sample comments
      formattedComments = posts.slice(0, 3).map(post => {
        let content = post?.content || '';
        // Include up to 2 replies if present
        if (Array.isArray(post.children) && post.children.length > 0) {
          const replies = post.children.slice(0, 2).map(child => `  - Reply: "${child?.content || ''}"`).join('\n');
          content += `\n${replies}`;
        }
        return `"${content}"`;
      }).join('\n\n');
    } else {
      formattedComments = 'No comments available.';
    }

    // Build the formattedData string
    const formattedData = `Event: ${eventName}
Question Type: ${questionType}
Description: ${description}

Total Responses: ${totalResponses}

Options:
${formattedOptions}

User Comments:
${formattedComments}`;

    // Create multiple choice prompt template
    const promptTemplate = `You are an expert data analyst analyzing a Multiple Choice survey question.
This type of survey allows respondents to select from predefined options. Analyze the following data and provide a structured report in markdown format:

## Event Analysis Data
${formattedData}

## Analysis Instructions
1. Focus on patterns and correlations between chosen options, not just listing percentages
2. Identify potential reasons WHY certain options were preferred over others
3. Analyze engagement patterns - which options generated more discussion or interaction?
4. Evaluate the diversity of preferences and what it reveals about consensus or division
5. Look for clusters or groupings of similar options that might indicate underlying themes
6. Analyze options with unexpectedly high or low selection rates and what that might indicate
7. Consider what the distribution reveals about audience priorities, values, and decision-making

## Report Format
Please format your response as follows:

# Executive Summary
[1-2 paragraph summary focusing on the key narrative emerging from responses]

# Analysis
- [Bullet points with insights about response patterns and their significance]
- [Include analysis of both explicit statements and implicit meanings]
- [Note any emotional undertones or sentiment patterns]
- [Highlight any subtle differences or variations in how respondents expressed themselves]

# Key Themes
- [List 3-5 major themes with examples from the responses]
- [Focus on underlying concerns, values, and priorities]
- [Include both positive and constructive feedback themes]
- [If engagement is present and nonzero, highlight which themes generated the most engagement]

# Recommendations
- [Provide 3-5 actionable next steps based on the feedback]
- [Include specific suggestions for addressing concerns]
- [Recommend strategies for leveraging positive feedback]
- [Suggest follow-up questions to explore identified themes further]

# Limitations
- [List any limitations in the data that affect the depth or breadth of analysis]
- [Suggest what additional data would be helpful]
- [Note any potential biases or gaps in the responses]

# Survey Design Feedback
- [Provide suggestions for improving the survey question or format]
- [Suggest how to generate richer, more actionable, or more diverse responses]

Format your response with these exact section headings using single # for each section. Ensure each section has substantive content.`;

    console.log('✅ Multiple choice prompt generated:', {
      promptLength: promptTemplate.length,
      timestamp: new Date().toISOString()
    });

    return promptTemplate;
  } catch (error) {
    console.error(`❌ Error in generateMultipleChoicePrompt: ${error.message}`);
    return null;
  }
};